/**
 * PermissionsDashboard - Visual overview of the entire permission system
 * 
 * Shows:
 * - Role templates matrix (roles × menu items)
 * - Authority levels matrix (levels × permissions)
 * - Department & sub-department mappings
 * - Interactive help explaining the philosophy
 */

import React, { useState, useEffect, useMemo } from 'react';
import { useMsal } from '@azure/msal-react';
import { authenticatedApiCall } from '../../utils/apiClient';
import { MENU_STRUCTURE } from '../../config/menuStructure';
import { RESOURCE_DEPARTMENTS, RESOURCE_SUB_DEPARTMENTS } from '../../config/resourceAccess';
import { ListenButton } from '../common/AudioGuide';
import HelpWithVideoTour from '../Help/HelpWithVideoTour';
import { usePresence, ActiveViewers } from '../common/ViewingTemplate';

const DEPT_COLORS = {
  1: '#3498db',  // Construction
  2: '#2ecc71',  // Accounting
  3: '#e67e22',  // Estimating
  4: '#9b59b6',  // Procurement
  5: '#e74c3c',  // Safety
  6: '#1abc9c',  // Quality
  7: '#34495e',  // Operations
  8: '#95a5a6',  // Admin
  9: '#f39c12',  // Sales
  10: '#d35400', // Tool Crib
  11: '#2980b9', // Resources
};

const AUTHORITY_COLORS = {
  'System': '#e74c3c',
  'Upper Management': '#e67e22',
  'Manager': '#f39c12',
  'Lead': '#3498db',
  'Staff': '#95a5a6',
};

const SCOPE_LABELS = {
  'all_projects_all_trades': 'All Projects, All Trades',
  'all_projects': 'All Projects',
  'assigned_area_all_trades': 'Assigned Area, All Trades',
  'assigned_projects_all_trades': 'Assigned Projects, All Trades',
  'assigned_projects': 'Assigned Projects',
  'assigned_projects_assigned_trades': 'Assigned Projects, Assigned Trades',
};

// Flatten menu structure to get all leaf item IDs
const flattenMenu = (items, depth = 0, parentPath = '') => {
  const result = [];
  for (const item of items) {
    const path = parentPath ? `${parentPath} > ${item.label}` : item.label;
    result.push({ id: item.id, label: item.label, path, depth });
    if (item.subItems) {
      result.push(...flattenMenu(item.subItems, depth + 1, path));
    }
  }
  return result;
};

// Get all unique top-level menu section IDs
const getTopLevelMenuIds = () => {
  return MENU_STRUCTURE.map(item => item.id);
};

const HelpModal = ({ onClose }) => (
  <div style={{
    position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.6)',
    display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 10000
  }} onClick={onClose}>
    <div style={{
      backgroundColor: '#fff', borderRadius: 16, maxWidth: 800, width: '90%',
      maxHeight: '85vh', overflowY: 'auto', padding: '40px 48px',
      boxShadow: '0 24px 80px rgba(0,0,0,0.3)'
    }} onClick={e => e.stopPropagation()}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 32 }}>
        <h2 style={{ margin: 0, fontSize: 24, color: '#1a1a2e' }}>Permission System Philosophy</h2>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <ListenButton compact guideId="permissions-dashboard-help" title="Permissions Dashboard Help" />
          <button onClick={onClose} style={{
            background: 'none', border: 'none', fontSize: 24, cursor: 'pointer',
            color: '#999', padding: '4px 8px', lineHeight: 1
          }}>&times;</button>
        </div>
      </div>

      <div style={{ lineHeight: 1.8, color: '#333', fontSize: 15 }}>
        <HelpWithVideoTour guideId="permissions-dashboard-help" title="Permissions Dashboard Help" accent="#e74c3c">
        <h3 style={{ color: '#e74c3c', marginTop: 0 }}>Three-Layer Defense-in-Depth</h3>
        <p>The permission system uses three independent layers, each answering a different question:</p>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 16, margin: '24px 0' }}>
          {[
            { layer: 'Layer 1', title: 'Department', question: 'Can they see this MODULE?', color: '#3498db',
              desc: 'Controls top-level navigation visibility. A Construction user can see Progress Tracking; an Accounting user can see Time Tracker.' },
            { layer: 'Layer 2', title: 'Sub-Department', question: 'Can they see this DISCIPLINE?', color: '#2ecc71',
              desc: 'Granular access within a department. A Piping user sees the Piping tracker; a Mechanical user sees Mechanical.' },
            { layer: 'Layer 3', title: 'Authority Level', question: 'What can they DO?', color: '#e67e22',
              desc: 'Controls actions: view, edit, import, export, delete. Staff can edit; Leads can import/export; Managers get analytics.' },
          ].map(l => (
            <div key={l.layer} style={{
              border: `2px solid ${l.color}`, borderRadius: 12, padding: 20,
              backgroundColor: `${l.color}10`
            }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: l.color, textTransform: 'uppercase', letterSpacing: 1 }}>{l.layer}</div>
              <div style={{ fontSize: 16, fontWeight: 700, margin: '8px 0 4px', color: '#1a1a2e' }}>{l.title}</div>
              <div style={{ fontSize: 13, fontWeight: 600, color: l.color, marginBottom: 8 }}>{l.question}</div>
              <div style={{ fontSize: 13, color: '#555' }}>{l.desc}</div>
            </div>
          ))}
        </div>

        <h3 style={{ color: '#2c3e50' }}>Key Concepts</h3>
        <ul style={{ paddingLeft: 20 }}>
          <li><strong>Role Template</strong> &mdash; Defines a user's department, default authority level, scope, and which menu sections they can see. Examples: Project Manager, Technical Coordinator, Cost Clerk.</li>
          <li><strong>Authority Level</strong> &mdash; Separate from role. Controls permissions (actions). Can be overridden per-user without changing their role. Hierarchy: System &gt; Upper Management &gt; Manager &gt; Lead &gt; Staff.</li>
          <li><strong>Menu Items</strong> &mdash; The <code>menu_items</code> array on a role template lists which top-level sections the user can see. <code>"*"</code> means all sections their department allows.</li>
          <li><strong>Scope</strong> &mdash; Controls which projects the user can access: all projects, assigned projects only, or assigned projects with assigned trades only.</li>
          <li><strong>Superadmin</strong> &mdash; Bypasses ALL checks on both frontend and backend. Two protected superadmins cannot have their role changed.</li>
        </ul>

        <h3 style={{ color: '#2c3e50' }}>Frontend vs Backend</h3>
        <p>
          The <strong>frontend</strong> (sidebar menu) hides items users shouldn't see &mdash; this is a UX convenience, not security.
          The <strong>backend</strong> (API middleware) enforces the rules with 403 responses &mdash; this is the real security boundary.
          Both checks must pass for access to be granted.
        </p>

        <h3 style={{ color: '#2c3e50' }}>How Menu Inheritance Works</h3>
        <p>
          If a role has <code>"estimation"</code> in its menu_items, all children and grandchildren under the Estimation
          section are automatically visible (assuming department/sub-department checks pass). You don't need to list
          every sub-item individually.
        </p>

        <h3 style={{ color: '#e74c3c', marginTop: 32 }}>Progress Tracking Permissions (pt_*)</h3>
        <p>Progress Tracking uses fine-grained permissions that control specific actions within the tracker matrix:</p>

        <table style={{ width: '100%', borderCollapse: 'collapse', margin: '16px 0', fontSize: 13 }}>
          <thead>
            <tr style={{ backgroundColor: '#1a1a2e', color: '#fff' }}>
              <th style={{ padding: '8px 12px', textAlign: 'left' }}>Permission</th>
              <th style={{ padding: '8px 12px', textAlign: 'left' }}>Controls</th>
              <th style={{ padding: '8px 12px', textAlign: 'left' }}>Min Authority</th>
            </tr>
          </thead>
          <tbody>
            {[
              ['pt_analytics', 'View analytics and progress trends', 'Staff'],
              ['pt_edit', 'Edit cell values in the tracker', 'Lead'],
              ['pt_edit_all', 'Edit all fields (including normally locked ones)', 'Lead'],
              ['pt_import', 'Upload/import Excel data into the tracker', 'Lead'],
              ['pt_export', 'Export tracker data to Excel', 'Lead'],
              ['pt_template', 'Download blank import template', 'Lead'],
              ['pt_delete', 'Delete rows (UI checkboxes + Action "d" in import)', 'Upper Management'],
              ['pt_columnconfig', 'Configure visible columns', 'Manager'],
              ['pt_roc', 'Configure Rules of Credit', 'Manager'],
              ['pt_sfratio', 'Configure Shop/Field ratios', 'Manager'],
              ['pt_matbore', 'Configure Material & Bore percentages', 'Manager'],
            ].map(([perm, desc, level], idx) => (
              <tr key={perm} style={{ backgroundColor: idx % 2 === 0 ? '#fff' : '#f8f9fa' }}>
                <td style={{ padding: '6px 12px', fontFamily: 'monospace', fontSize: 12, fontWeight: 600 }}>{perm}</td>
                <td style={{ padding: '6px 12px' }}>{desc}</td>
                <td style={{ padding: '6px 12px' }}>
                  <span style={{
                    padding: '2px 8px', borderRadius: 4, fontSize: 11, fontWeight: 600,
                    backgroundColor: level === 'Upper Management' ? '#e67e2218' : level === 'Manager' ? '#f39c1218' : level === 'Lead' ? '#3498db18' : '#95a5a618',
                    color: level === 'Upper Management' ? '#e67e22' : level === 'Manager' ? '#f39c12' : level === 'Lead' ? '#3498db' : '#95a5a6',
                  }}>{level}+</span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        <div style={{ backgroundColor: '#fff3cd', border: '1px solid #ffc107', borderRadius: 8, padding: '12px 16px', margin: '16px 0' }}>
          <strong style={{ color: '#856404' }}>Important Nuance &mdash; Deleting Rows:</strong>
          <ul style={{ margin: '8px 0 0', paddingLeft: 20, color: '#856404' }}>
            <li><strong>Delete checkboxes</strong> in the tracker UI require <code>pt_delete</code> (Upper Management+)</li>
            <li><strong>Action "d" in Excel import</strong> also requires <code>pt_delete</code> &mdash; rows marked for deletion will be skipped if the user lacks this permission</li>
            <li>Deleting rows permanently removes all progress history (shop/field trends) &mdash; this cannot be undone</li>
            <li>Users without <code>pt_delete</code> can still update and insert rows via import (only requires <code>pt_import</code>)</li>
          </ul>
        </div>

        <div style={{ backgroundColor: '#d4edda', border: '1px solid #28a745', borderRadius: 8, padding: '12px 16px', margin: '16px 0' }}>
          <strong style={{ color: '#155724' }}>Import/Export Workflow:</strong>
          <ul style={{ margin: '8px 0 0', paddingLeft: 20, color: '#155724' }}>
            <li><strong>Blank Template</strong> (<code>pt_template</code>) &mdash; Use for initial data entry. Does NOT include UUID or Action columns.</li>
            <li><strong>Data Export</strong> (<code>pt_export</code>) &mdash; Exports current data WITH UUID and Action columns. Use this for re-imports and updates.</li>
            <li><strong>Re-import</strong> &mdash; When the file has a UUID column, rows with matching UUIDs are updated (not duplicated). Only rows without UUIDs are inserted as new.</li>
            <li><strong>Avoid duplicates</strong> &mdash; Always re-import from an exported file (with UUIDs), not from the blank template, when updating existing data.</li>
          </ul>
        </div>
        </HelpWithVideoTour>
      </div>
    </div>
  </div>
);

const PermissionsDashboard = ({ embedded = false }) => {
  const { instance, accounts } = useMsal();
  const { viewers, isConnected, connectionState } = usePresence('permissionsDashboard', 'global', true);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('roles');
  const [showHelp, setShowHelp] = useState(false);
  const [hoveredCell, setHoveredCell] = useState(null);
  const [selectedDept, setSelectedDept] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const response = await authenticatedApiCall(instance, accounts, 'GET', '/auth/permissions-dashboard');
        if (response.data.success) {
          setData(response.data.data);
        } else {
          setError('Failed to fetch data');
        }
      } catch (err) {
        console.error('Error:', err);
        setError(err.response?.status === 403 ? 'Access denied. Superadmin or Operations access required.' : 'Failed to load permissions data.');
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [instance, accounts]);

  // Build menu items list for the roles matrix
  const topLevelMenuItems = useMemo(() => {
    return MENU_STRUCTURE
      .filter(item => item.id !== 'home' && item.id !== 'department-selector')
      .map(item => ({
        id: item.id,
        label: item.label,
        icon: item.icon,
        department: item.department,
        requiresProject: item.requiresProject,
        hideInProject: item.hideInProject,
        subItemCount: item.subItems ? item.subItems.length : 0,
        deptRestriction: RESOURCE_DEPARTMENTS[item.id] || null,
      }));
  }, []);

  // Filtered roles
  const filteredRoles = useMemo(() => {
    if (!data?.roles) return [];
    let roles = data.roles;
    if (selectedDept !== 'all') {
      const deptId = parseInt(selectedDept);
      roles = roles.filter(r => r.department_id === deptId || r.department_id === null);
    }
    if (searchTerm) {
      const s = searchTerm.toLowerCase();
      roles = roles.filter(r => r.display_name.toLowerCase().includes(s) || r.id.toLowerCase().includes(s));
    }
    return roles;
  }, [data, selectedDept, searchTerm]);

  // Build sub-department restriction mapping for the resource access tab
  const subDeptItems = useMemo(() => {
    const items = [];
    for (const [menuId, subDeptIds] of Object.entries(RESOURCE_SUB_DEPARTMENTS)) {
      const menuItem = flattenMenu(MENU_STRUCTURE).find(m => m.id === menuId);
      items.push({
        menuId,
        label: menuItem?.label || menuId,
        path: menuItem?.path || menuId,
        subDeptIds,
      });
    }
    return items;
  }, []);

  if (loading) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: 400, color: '#6c757d' }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{ fontSize: 48, marginBottom: 16, animation: 'spin 1s linear infinite' }}>⚙️</div>
          <div>Loading permissions data...</div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ padding: 48, textAlign: 'center' }}>
        <div style={{ fontSize: 48, marginBottom: 16 }}>🔒</div>
        <h2 style={{ color: '#e74c3c' }}>Access Denied</h2>
        <p style={{ color: '#6c757d' }}>{error}</p>
      </div>
    );
  }

  const deptMap = {};
  data.departments.forEach(d => { deptMap[d.id] = d.name; });

  const subDeptMap = {};
  data.subDepartments.forEach(sd => { subDeptMap[sd.id] = sd.name; });

  const renderRolesMatrix = () => {
    const roles = filteredRoles;
    const menuCols = topLevelMenuItems;

    return (
      <div style={{ overflowX: 'auto' }}>
        <table style={{ borderCollapse: 'collapse', width: '100%', fontSize: 13 }}>
          <thead>
            <tr style={{ backgroundColor: '#1a1a2e', color: '#fff' }}>
              <th style={{ ...thStyle, minWidth: 160, position: 'sticky', left: 0, zIndex: 3, backgroundColor: '#1a1a2e' }}>Role</th>
              <th style={{ ...thStyle, minWidth: 110 }}>Department</th>
              <th style={{ ...thStyle, minWidth: 100 }}>Authority</th>
              <th style={{ ...thStyle, minWidth: 140 }}>Scope</th>
              {menuCols.map(m => (
                <th key={m.id} style={{
                  ...thStyle, minWidth: 44, maxWidth: 44, textAlign: 'center', padding: '8px 4px',
                  writingMode: 'vertical-rl', transform: 'rotate(180deg)', height: 130,
                  fontSize: 11, fontWeight: 600,
                  backgroundColor: m.deptRestriction ? `${DEPT_COLORS[m.deptRestriction[0]]}22` : '#1a1a2e',
                  color: '#fff'
                }} title={`${m.label}${m.deptRestriction ? ` (Dept: ${m.deptRestriction.map(d => deptMap[d] || d).join(', ')})` : ''}`}>
                  <span>{m.icon} {m.label}</span>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {roles.map((role, idx) => {
              const menuItems = role.menu_items || [];
              const hasWildcard = menuItems.includes('*');
              const deptColor = DEPT_COLORS[role.department_id] || '#666';
              const authColor = AUTHORITY_COLORS[role.authority_level] || '#666';

              return (
                <tr key={role.id} style={{ backgroundColor: idx % 2 === 0 ? '#fff' : '#f8f9fa' }}>
                  <td style={{
                    ...tdStyle, fontWeight: 600, position: 'sticky', left: 0, zIndex: 2,
                    backgroundColor: idx % 2 === 0 ? '#fff' : '#f8f9fa',
                    borderLeft: `4px solid ${deptColor}`
                  }}>
                    <div style={{ fontSize: 13 }}>{role.display_name}</div>
                    <div style={{ fontSize: 10, color: '#999', fontWeight: 400, fontFamily: 'monospace' }}>{role.id}</div>
                  </td>
                  <td style={tdStyle}>
                    <span style={{
                      padding: '2px 8px', borderRadius: 4, fontSize: 11, fontWeight: 600,
                      backgroundColor: `${deptColor}18`, color: deptColor, border: `1px solid ${deptColor}40`
                    }}>
                      {deptMap[role.department_id] || '—'}
                    </span>
                  </td>
                  <td style={tdStyle}>
                    <span style={{
                      padding: '2px 8px', borderRadius: 4, fontSize: 11, fontWeight: 600,
                      backgroundColor: `${authColor}18`, color: authColor, border: `1px solid ${authColor}40`
                    }}>
                      {role.authority_level || '—'}
                    </span>
                  </td>
                  <td style={{ ...tdStyle, fontSize: 11, color: '#555' }}>
                    {SCOPE_LABELS[role.scope] || role.scope}
                  </td>
                  {menuCols.map(m => {
                    const hasAccess = hasWildcard || menuItems.includes(m.id);
                    const deptRestriction = m.deptRestriction;
                    const deptBlocked = deptRestriction && role.department_id && !deptRestriction.includes(role.department_id);
                    const effectiveAccess = hasAccess && !deptBlocked;
                    const cellKey = `${role.id}-${m.id}`;
                    const isHovered = hoveredCell === cellKey;

                    return (
                      <td
                        key={m.id}
                        style={{
                          ...tdStyle, textAlign: 'center', padding: '4px 2px', cursor: 'default',
                          backgroundColor: isHovered
                            ? (effectiveAccess ? '#d4edda' : '#f8d7da')
                            : (effectiveAccess ? '#e8f5e9' : 'transparent'),
                          transition: 'background-color 0.1s'
                        }}
                        onMouseEnter={() => setHoveredCell(cellKey)}
                        onMouseLeave={() => setHoveredCell(null)}
                        title={`${role.display_name} → ${m.label}: ${effectiveAccess ? '✓ Has access' : hasAccess && deptBlocked ? '✗ Blocked by dept restriction' : '✗ Not in menu_items'}`}
                      >
                        {effectiveAccess ? (
                          <span style={{ color: '#28a745', fontSize: 16, fontWeight: 700 }}>✓</span>
                        ) : hasAccess && deptBlocked ? (
                          <span style={{ color: '#dc3545', fontSize: 12 }} title="Has menu item but blocked by department restriction">⊘</span>
                        ) : (
                          <span style={{ color: '#dee2e6' }}>–</span>
                        )}
                      </td>
                    );
                  })}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    );
  };

  const renderAuthorityMatrix = () => {
    const levels = data.authorityLevels;
    const allPerms = new Set();
    levels.forEach(l => {
      (l.permissions || []).forEach(p => { if (p !== '*') allPerms.add(p); });
    });
    const permList = Array.from(allPerms).sort();
    const hasSystem = levels.some(l => (l.permissions || []).includes('*'));

    return (
      <div style={{ overflowX: 'auto' }}>
        <table style={{ borderCollapse: 'collapse', width: '100%', fontSize: 13 }}>
          <thead>
            <tr style={{ backgroundColor: '#1a1a2e', color: '#fff' }}>
              <th style={{ ...thStyle, minWidth: 200, position: 'sticky', left: 0, zIndex: 3, backgroundColor: '#1a1a2e' }}>Permission</th>
              {levels.map(l => (
                <th key={l.id} style={{
                  ...thStyle, textAlign: 'center', minWidth: 120,
                  backgroundColor: `${AUTHORITY_COLORS[l.name] || '#666'}cc`, color: '#fff'
                }}>
                  <div style={{ fontSize: 13, fontWeight: 700 }}>{l.name}</div>
                  <div style={{ fontSize: 10, fontWeight: 400, opacity: 0.8 }}>Level {l.hierarchy_level}</div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {permList.map((perm, idx) => (
              <tr key={perm} style={{ backgroundColor: idx % 2 === 0 ? '#fff' : '#f8f9fa' }}>
                <td style={{
                  ...tdStyle, fontWeight: 600, fontFamily: 'monospace', fontSize: 12,
                  position: 'sticky', left: 0, zIndex: 2,
                  backgroundColor: idx % 2 === 0 ? '#fff' : '#f8f9fa'
                }}>
                  {perm}
                </td>
                {levels.map(l => {
                  const perms = l.permissions || [];
                  const has = perms.includes('*') || perms.includes(perm);
                  return (
                    <td key={l.id} style={{
                      ...tdStyle, textAlign: 'center',
                      backgroundColor: has ? '#e8f5e9' : 'transparent'
                    }}>
                      {has ? (
                        <span style={{ color: '#28a745', fontSize: 16, fontWeight: 700 }}>
                          {perms.includes('*') ? '★' : '✓'}
                        </span>
                      ) : (
                        <span style={{ color: '#dee2e6' }}>–</span>
                      )}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
        {hasSystem && (
          <div style={{ marginTop: 12, fontSize: 12, color: '#6c757d', display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={{ color: '#28a745', fontSize: 16, fontWeight: 700 }}>★</span> = Wildcard access (all permissions, including future ones)
          </div>
        )}
      </div>
    );
  };

  const renderResourceAccess = () => {
    const deptItems = Object.entries(RESOURCE_DEPARTMENTS).map(([menuId, deptIds]) => {
      const menuItem = flattenMenu(MENU_STRUCTURE).find(m => m.id === menuId);
      return { menuId, label: menuItem?.label || menuId, deptIds };
    });

    return (
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 32 }}>
        {/* Department Restrictions */}
        <div>
          <h3 style={{ fontSize: 16, color: '#1a1a2e', marginBottom: 16, display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={{ fontSize: 20 }}>🏢</span> Department Restrictions
          </h3>
          <p style={{ fontSize: 13, color: '#6c757d', marginBottom: 16 }}>
            Which departments can see each menu section. Defined in <code>resourceAccess.js → RESOURCE_DEPARTMENTS</code>.
          </p>
          <table style={{ borderCollapse: 'collapse', width: '100%', fontSize: 13 }}>
            <thead>
              <tr style={{ backgroundColor: '#1a1a2e', color: '#fff' }}>
                <th style={thStyle}>Menu Item</th>
                <th style={thStyle}>Allowed Departments</th>
              </tr>
            </thead>
            <tbody>
              {deptItems.map((item, idx) => (
                <tr key={item.menuId} style={{ backgroundColor: idx % 2 === 0 ? '#fff' : '#f8f9fa' }}>
                  <td style={{ ...tdStyle, fontWeight: 600 }}>{item.label}</td>
                  <td style={tdStyle}>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                      {item.deptIds.map(id => (
                        <span key={id} style={{
                          padding: '2px 8px', borderRadius: 4, fontSize: 11, fontWeight: 600,
                          backgroundColor: `${DEPT_COLORS[id] || '#666'}18`,
                          color: DEPT_COLORS[id] || '#666',
                          border: `1px solid ${DEPT_COLORS[id] || '#666'}40`
                        }}>
                          {deptMap[id] || `Dept ${id}`}
                        </span>
                      ))}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Sub-Department Restrictions */}
        <div>
          <h3 style={{ fontSize: 16, color: '#1a1a2e', marginBottom: 16, display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={{ fontSize: 20 }}>🔧</span> Sub-Department Restrictions
          </h3>
          <p style={{ fontSize: 13, color: '#6c757d', marginBottom: 16 }}>
            Discipline-level access for Progress Tracking. Defined in <code>resourceAccess.js → RESOURCE_SUB_DEPARTMENTS</code>.
          </p>
          <table style={{ borderCollapse: 'collapse', width: '100%', fontSize: 13 }}>
            <thead>
              <tr style={{ backgroundColor: '#1a1a2e', color: '#fff' }}>
                <th style={thStyle}>Menu Item</th>
                <th style={thStyle}>Required Sub-Department</th>
              </tr>
            </thead>
            <tbody>
              {subDeptItems.map((item, idx) => (
                <tr key={item.menuId} style={{ backgroundColor: idx % 2 === 0 ? '#fff' : '#f8f9fa' }}>
                  <td style={{ ...tdStyle, fontWeight: 600 }}>{item.label}</td>
                  <td style={tdStyle}>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                      {item.subDeptIds.map(id => (
                        <span key={id} style={{
                          padding: '2px 8px', borderRadius: 4, fontSize: 11, fontWeight: 600,
                          backgroundColor: '#3498db18', color: '#3498db', border: '1px solid #3498db40'
                        }}>
                          {subDeptMap[id] || `Sub-dept ${id}`}
                        </span>
                      ))}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  const renderScopeOverview = () => {
    const scopeGroups = {};
    data.roles.forEach(r => {
      const scope = r.scope || 'unset';
      if (!scopeGroups[scope]) scopeGroups[scope] = [];
      scopeGroups[scope].push(r);
    });

    return (
      <div>
        <p style={{ fontSize: 13, color: '#6c757d', marginBottom: 24 }}>
          Scope controls which projects a role can access. More restrictive scopes limit visibility to assigned projects and/or assigned trades (sub-departments).
        </p>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: 16 }}>
          {Object.entries(SCOPE_LABELS).map(([scopeKey, label]) => {
            const roles = scopeGroups[scopeKey] || [];
            if (roles.length === 0) return null;
            const isWide = scopeKey.includes('all_projects');
            return (
              <div key={scopeKey} style={{
                border: '1px solid #e9ecef', borderRadius: 12, padding: 20,
                backgroundColor: '#fff',
                borderLeft: `4px solid ${isWide ? '#e67e22' : '#3498db'}`
              }}>
                <div style={{ fontSize: 14, fontWeight: 700, color: '#1a1a2e', marginBottom: 4 }}>{label}</div>
                <div style={{ fontSize: 11, color: '#999', fontFamily: 'monospace', marginBottom: 12 }}>{scopeKey}</div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                  {roles.map(r => (
                    <span key={r.id} style={{
                      padding: '3px 10px', borderRadius: 6, fontSize: 12, fontWeight: 500,
                      backgroundColor: `${DEPT_COLORS[r.department_id] || '#666'}12`,
                      color: DEPT_COLORS[r.department_id] || '#666',
                      border: `1px solid ${DEPT_COLORS[r.department_id] || '#666'}30`
                    }}>
                      {r.display_name}
                    </span>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  const tabs = [
    { id: 'roles', label: 'Role × Menu Matrix', icon: '📋' },
    { id: 'authority', label: 'Authority × Permissions', icon: '🔑' },
    { id: 'resources', label: 'Resource Access Maps', icon: '🗺️' },
    { id: 'scopes', label: 'Scope Overview', icon: '🎯' },
  ];

  const stats = data ? {
    roles: data.roles.length,
    departments: data.departments.length,
    subDepartments: data.subDepartments.length,
    authorityLevels: data.authorityLevels.length,
    totalPermissions: new Set(data.authorityLevels.flatMap(l => l.permissions || []).filter(p => p !== '*')).size,
  } : {};

  return (
    <div style={{ padding: embedded ? '8px 0' : '24px 32px', maxWidth: 1600, margin: '0 auto' }}>
      {showHelp && <HelpModal onClose={() => setShowHelp(false)} />}

      {/* Standalone (PMO) view is read-only reference; management lives in the Admin panel */}
      {!embedded && (
        <div style={{
          marginBottom: 20, padding: '10px 16px', borderRadius: 8,
          backgroundColor: '#e7f1ff', border: '1px solid #b6d4fe',
          fontSize: 13, color: '#084298', lineHeight: 1.6,
        }}>
          📍 This is a <strong>read-only reference view</strong>. Editing roles, authority levels and
          overrides — plus the tester and the full plain-English guide — now live in one place:
          <strong> Admin Panel → Permissions</strong>.
        </div>
      )}

      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 24 }}>
        <div>
          <h1 style={{ margin: 0, fontSize: 24, color: '#1a1a2e', display: 'flex', alignItems: 'center', gap: 12 }}>
            <span style={{ fontSize: 28 }}>🛡️</span>
            Permission System Dashboard
          </h1>
          <p style={{ margin: '4px 0 0', color: '#6c757d', fontSize: 14 }}>
            Complete overview of roles, departments, authority levels, and access rules
          </p>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <ActiveViewers viewers={viewers} isConnected={isConnected} connectionState={connectionState} maxVisible={4} showConnectionStatus={true} />
          <button
            onClick={() => setShowHelp(true)}
            style={{
              padding: '10px 20px', backgroundColor: '#1a1a2e', color: '#fff',
              border: 'none', borderRadius: 8, cursor: 'pointer', fontSize: 14,
              fontWeight: 600, display: 'flex', alignItems: 'center', gap: 8,
              transition: 'background-color 0.2s'
            }}
            onMouseEnter={e => e.currentTarget.style.backgroundColor = '#e74c3c'}
            onMouseLeave={e => e.currentTarget.style.backgroundColor = '#1a1a2e'}
          >
            <span>❓</span> Philosophy &amp; Help
          </button>
        </div>
      </div>

      {/* Stats Bar */}
      <div style={{
        display: 'flex', gap: 16, marginBottom: 24, flexWrap: 'wrap'
      }}>
        {[
          { label: 'Roles', value: stats.roles, color: '#3498db' },
          { label: 'Departments', value: stats.departments, color: '#2ecc71' },
          { label: 'Sub-Departments', value: stats.subDepartments, color: '#e67e22' },
          { label: 'Authority Levels', value: stats.authorityLevels, color: '#9b59b6' },
          { label: 'Unique Permissions', value: stats.totalPermissions, color: '#e74c3c' },
        ].map(s => (
          <div key={s.label} style={{
            flex: 1, minWidth: 140, padding: '14px 18px', borderRadius: 10,
            backgroundColor: '#fff', border: '1px solid #e9ecef',
            borderBottom: `3px solid ${s.color}`
          }}>
            <div style={{ fontSize: 24, fontWeight: 700, color: s.color }}>{s.value}</div>
            <div style={{ fontSize: 12, color: '#6c757d', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5 }}>{s.label}</div>
          </div>
        ))}
      </div>

      {/* Tab Navigation */}
      <div style={{
        display: 'flex', gap: 4, marginBottom: 20,
        borderBottom: '2px solid #e9ecef', paddingBottom: 0
      }}>
        {tabs.map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            style={{
              padding: '10px 20px', border: 'none', cursor: 'pointer',
              fontSize: 13, fontWeight: 600, borderRadius: '8px 8px 0 0',
              backgroundColor: activeTab === tab.id ? '#1a1a2e' : 'transparent',
              color: activeTab === tab.id ? '#fff' : '#666',
              transition: 'all 0.2s',
              display: 'flex', alignItems: 'center', gap: 6,
              marginBottom: -2,
              borderBottom: activeTab === tab.id ? '2px solid #1a1a2e' : '2px solid transparent',
            }}
            onMouseEnter={e => { if (activeTab !== tab.id) e.currentTarget.style.backgroundColor = '#f0f0f0'; }}
            onMouseLeave={e => { if (activeTab !== tab.id) e.currentTarget.style.backgroundColor = 'transparent'; }}
          >
            <span>{tab.icon}</span> {tab.label}
          </button>
        ))}
      </div>

      {/* Filters (only for roles tab) */}
      {activeTab === 'roles' && (
        <div style={{ display: 'flex', gap: 12, marginBottom: 16, alignItems: 'center' }}>
          <select
            value={selectedDept}
            onChange={e => setSelectedDept(e.target.value)}
            style={{
              padding: '8px 12px', borderRadius: 6, border: '1px solid #ddd',
              fontSize: 13, backgroundColor: '#fff', cursor: 'pointer'
            }}
          >
            <option value="all">All Departments</option>
            {data.departments.map(d => (
              <option key={d.id} value={d.id}>{d.name}</option>
            ))}
          </select>
          <input
            type="text"
            placeholder="Search roles..."
            value={searchTerm}
            onChange={e => setSearchTerm(e.target.value)}
            style={{
              padding: '8px 12px', borderRadius: 6, border: '1px solid #ddd',
              fontSize: 13, width: 200
            }}
          />
          <div style={{ marginLeft: 'auto', fontSize: 12, color: '#999' }}>
            Showing {filteredRoles.length} of {data.roles.length} roles
          </div>
        </div>
      )}

      {/* Tab Content */}
      <div style={{
        backgroundColor: '#fff', borderRadius: 12, border: '1px solid #e9ecef',
        boxShadow: '0 2px 12px rgba(0,0,0,0.04)', overflow: 'hidden'
      }}>
        <div style={{ padding: activeTab === 'roles' || activeTab === 'authority' ? 0 : 24 }}>
          {activeTab === 'roles' && renderRolesMatrix()}
          {activeTab === 'authority' && renderAuthorityMatrix()}
          {activeTab === 'resources' && renderResourceAccess()}
          {activeTab === 'scopes' && renderScopeOverview()}
        </div>
      </div>

      {/* Legend */}
      <div style={{
        marginTop: 20, padding: '16px 20px', backgroundColor: '#f8f9fa',
        borderRadius: 8, border: '1px solid #e9ecef',
        display: 'flex', gap: 24, flexWrap: 'wrap', fontSize: 12, color: '#555'
      }}>
        <div style={{ fontWeight: 700, color: '#1a1a2e' }}>Legend:</div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
          <span style={{ color: '#28a745', fontWeight: 700 }}>✓</span> Has access
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
          <span style={{ color: '#dc3545' }}>⊘</span> Menu item present but blocked by department restriction
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
          <span style={{ color: '#dee2e6' }}>–</span> No access
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
          <span style={{ color: '#28a745', fontWeight: 700 }}>★</span> Wildcard (all permissions)
        </div>
      </div>
    </div>
  );
};

const thStyle = {
  padding: '10px 12px', textAlign: 'left', fontWeight: 600,
  fontSize: 12, borderBottom: '2px solid #dee2e6',
  whiteSpace: 'nowrap', userSelect: 'none'
};

const tdStyle = {
  padding: '8px 12px', borderBottom: '1px solid #f0f0f0',
  whiteSpace: 'nowrap'
};

export default PermissionsDashboard;
